//// @ts-check
// REEnable TypeScript checking with JSDoc comments

import { PageNumber } from 'components';
import BaseTable from 'components/Tables/BaseTable';
import LoadingDots from 'components/UI/LoadingDots';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { describeSirenTone } from 'utils/devices/sirenTone.utils';

/**
 * this is a single data point in the device history
 * @typedef {Object} DeviceHistoryDataPoint
 * @property {number} ts - Timestamp of the event
 * @property {string} node_id - Device ID
 * @property {number} status - Status code (-3: API Error, -2: Offline, -1: Maintenance, 0: Open/Idle, 1: Closed/Active)
 * @property {string} source - Source of the status update
 * @property {string} comments - Additional comments or notes
 */

/**
 * this is the props for the DeviceHistory component
 * @typedef {Object} DeviceHistoryProps
 * @property {DeviceHistoryDataPoint[]} data - Array of device history data points
 * @property {string} [title="Device History"] - Title of the history section
 * @property {string} [subTitle] - Subtitle of the history section
 * @property {string} deviceType - Type of the device (e.g., "Technology Bridge_Siren/Giant Voice")
 * @property {boolean} [hideHeader=false] - Flag to hide the header section
 * @property {string} [tablePreferenceKey] - Stable key for persisted table preferences
 * @property {string} [tableOptionsClassName] - Spacing classes for the table options row
 * @property {boolean} [loading=false] - A page of history is in flight
 * @property {boolean} [hasMore=false] - The backend reported another page behind this one
 * @property {() => void} [onLoadMore] - Asks the owning screen for the next page
 * @property {{message: string} | null} [error] - The last fetch failed; shown instead of the empty state
 */

/**
 * this is the signature for the cell context passed to the Cell renderers
 * @typedef {Object} CellContext
 * @property {() => any} getValue - Function to get the cell value
 */

/**
 * this is the definition for a table column
 * @typedef {Object} TableColumn
 * @property {string} accessorKey - Key to access data in row object
 * @property {string} header - Column header text
 * @property {(props: {cell: CellContext}) => React.ReactElement} Cell - Cell renderer function
 */

/**
 * this is the definition for a table row
 * @typedef {Object} TableRow
 * @property {number} ts - Timestamp
 * @property {string} node_id - Device ID
 * @property {number} status - Status code
 * @property {string} source - Source
 * @property {string} comments - Comments
 * @property {string} statusText - Computed status text for display
 */

/**
 * DeviceHistory component to display device history data in a paginated table.
 * @param {DeviceHistoryProps} props - Props for the DeviceHistory component
 * @returns {React.ReactElement} The rendered DeviceHistory component
 */
export default function DeviceHistory({
  data,
  title = 'Device History',
  subTitle,
  deviceType,
  hideHeader = false,
  tablePreferenceKey,
  tableOptionsClassName = '',
  loading = false,
  hasMore = false,
  onLoadMore,
  error = null,
}) {
  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      require('hooks/useRenderTracker').default('DeviceHistory');
    }
  } catch (e) {}
  const [pageNumber, setPageNumber] = useState(0);
  // Sirens get a Tone column: which tone or message each activation played.
  const isSirenHistory = deviceType === 'Technology Bridge_Siren/Giant Voice';
  // The hook hands back the API's own wording when it has one; this is the fallback.
  const historyErrorText = error?.message || 'The history could not be loaded.';

  /**
   * Map raw status codes into UI-friendly labels for this history view.
   * @param {DeviceHistoryDataPoint} point - {@link DeviceHistoryDataPoint} to get status text for
   * @returns {string} User-friendly status text
   */
  const getStatusText = (point) => {
    if (!point) return '';

    const errorMsg = 'Error';
    const positiveMsg = deviceType === 'Technology Bridge_Siren/Giant Voice' ? 'Active' : 'Closed';
    const negativeMsg = deviceType === 'Technology Bridge_Siren/Giant Voice' ? 'Idle' : 'Open';

    const maintenanceMsg = 'Maintenance';
    const offlineMsg = 'Offline';
    const apiErrorMsg = 'API Error';

    //MRC status codes:
    // 0 = Open
    // 1 = Closed

    // Technology Bridge Only
    // Flashing Light/Siren status codes:
    // 0 = Open/Idle
    // 1 = Closed/Active
    // -1 = maintenance
    // -2 = offline
    // -3 = API error

    return point.status === -3
      ? apiErrorMsg
      : point.status === -1
        ? maintenanceMsg
        : point.status === -2
          ? offlineMsg
          : point.status === 0
            ? negativeMsg
            : positiveMsg;
  };

  /**
   * Component to render a single data row
   * @param {{point: DeviceHistoryDataPoint}} props
   * @returns {React.ReactElement | null}
   */
  const DataRow = ({ point }) => {
    if (!point) return null;
    const statusText = getStatusText(point);

    return (
      <div className="flex-fill flex-basis-0">
        <div className="d-none d-lg-block">
          <div className="row text-center">
            <div className="col-2">
              <p>{new Date(point.ts).toLocaleString()}</p>
            </div>
            <div className="col-2">
              <p>{point.node_id}</p>
            </div>
            <div className="col-2">
              <p>{statusText}</p>
            </div>
            <div className="col-3">
              <p className="text-truncate" title={point.source}>
                {point.source}
              </p>
            </div>
            <div className="col-3">
              <p className="text-truncate" title={point.comments}>
                {point.comments}
              </p>
            </div>
          </div>
        </div>

        <div className="d-block d-lg-none">
          <div className="row p-1">
            <div>
              <p>
                Date/Time: <span>{dayjs(point.ts).format('MM/DD/YY, hh:mm A')}</span>
              </p>
            </div>
            <div>
              <p>
                Device ID: <span>{point.node_id}</span>
              </p>
            </div>
            <div>
              <p>
                Status: <span>{statusText}</span>
              </p>
            </div>
            {isSirenHistory && (
              <div>
                <p>
                  Tone: <span>{describeSirenTone(point)}</span>
                </p>
              </div>
            )}
            <div>
              <p>
                Source: <span>{point.source}</span>
              </p>
            </div>
            <div>
              <p className="m-0">Comments:</p>
              <p className="ms-2 text-wrap">{point.comments}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Keep pagination stable by slicing to the current page.
  /** @type {DeviceHistoryDataPoint[]} */
  const pagedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.slice(pageNumber * 6, pageNumber * 6 + 6);
  }, [data, pageNumber]);

  // Desktop layout uses BaseTable for consistent styling across the app.
  /** @type {TableColumn[]} */
  const columns = useMemo(
    () => [
      {
        accessorKey: 'ts',
        header: 'Date/Time',
        Cell: (/** @type {{cell: CellContext}} */ { cell }) => (
          <span>{dayjs(cell.getValue()).format('MM/DD/YY, hh:mm A')}</span>
        ),
      },
      {
        accessorKey: 'node_id',
        header: 'Device ID',
        Cell: (/** @type {{cell: CellContext}} */ { cell }) => <span>{cell.getValue()}</span>,
      },
      {
        accessorKey: 'statusText',
        header: 'Status',
        Cell: (/** @type {{cell: CellContext}} */ { cell }) => <span>{cell.getValue()}</span>,
      },
      ...(isSirenHistory
        ? [
            {
              accessorKey: 'toneText',
              header: 'Tone',
              Cell: (/** @type {{cell: CellContext}} */ { cell }) => (
                <span className="text-truncate d-inline-block w-100" title={cell.getValue()}>
                  {cell.getValue()}
                </span>
              ),
            },
          ]
        : []),
      {
        accessorKey: 'source',
        header: 'Source',
        Cell: (/** @type {{cell: CellContext}} */ { cell }) => (
          <span className="text-truncate d-inline-block w-100" title={cell.getValue()}>
            {cell.getValue()}
          </span>
        ),
      },
      {
        accessorKey: 'comments',
        header: 'Comments',
        Cell: (/** @type {{cell: CellContext}} */ { cell }) => (
          <span className="text-truncate d-inline-block w-100" title={cell.getValue()}>
            {cell.getValue()}
          </span>
        ),
      },
    ],
    [isSirenHistory],
  );

  // Precompute status label for the table to avoid repeated lookups.
  /** @type {TableRow[]} */
  const tableRows = useMemo(() => {
    if (!Array.isArray(pagedData)) return [];
    return pagedData.map((point) => ({
      ...point,
      statusText: getStatusText(point),
      toneText: isSirenHistory ? describeSirenTone(point) : undefined,
    }));
  }, [pagedData, deviceType, isSirenHistory]);

  return (
    <div className="d-flex flex-column align-items-center flex-fill w-100 rounded-2 bg-primary">
      {!hideHeader && (
        <>
          <h1 className="d-none d-lg-block fs-4">{title}</h1>
          <h4 className="d-block d-lg-none fs-6">{title}</h4>
          {subTitle && <h3 className="d-none d-lg-block fs-6">{subTitle}</h3>}
          {subTitle && (
            <p className="d-block d-lg-none border-bottom pb-2 w-100 text-center">{subTitle}</p>
          )}
        </>
      )}

      {/* List */}
      {data && (
        <div className="d-flex flex-column flex-grow-1 w-100 justify-content-start align-items-center">
          <div className="w-100 d-none d-lg-block">
            <BaseTable
              columns={columns}
              rows={tableRows}
              enableRowVirtualization={false}
              tablePreferenceKey={tablePreferenceKey}
              tableOptionsClassName={tableOptionsClassName}
              customStyles={{
                container: { backgroundColor: 'transparent' },
                header: { backgroundColor: '#2C373B' },
                cell: {
                  color: '#ffffff',
                  borderBottom: '1px solid #56577A',
                  padding: '8px',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </div>

          {data?.length > 0 && (
            <div className="d-flex d-lg-none">
              <DataRow point={data[pageNumber]} />
            </div>
          )}

          {data?.length === 0 &&
            (error ? (
              // A failed fetch is not an empty history: saying "NO DATA" here would
              // report a device as never activated when the request simply failed.
              <p className="text-danger m-0 py-2 text-center">{historyErrorText}</p>
            ) : loading ? (
              <LoadingDots className="py-2" label="Loading history" />
            ) : (
              <p>NO DATA</p>
            ))}

          <div className="d-none d-lg-block">
            <PageNumber
              pages={Math.ceil(data.length / 6)}
              value={pageNumber}
              onChange={setPageNumber}
            />
          </div>

          <div className="d-block d-lg-none">
            <PageNumber pages={data.length} value={pageNumber} onChange={setPageNumber} />
          </div>

          {/* One paging footer for every history surface. The page load holds ONE row
              per device, so the older rows are asked for here; the owning screen runs
              the fetch (useDeviceSeriesData) and this component stays presentational.
              `hasMore` is false after a failed page, so the button retries instead. */}
          {(hasMore || loading || error) && (
            <div className="d-flex flex-column align-items-center gap-1 py-1">
              {error && data?.length > 0 && (
                <span className="text-danger small text-center">{historyErrorText}</span>
              )}
              {onLoadMore && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light"
                  disabled={loading}
                  onClick={onLoadMore}
                >
                  {loading ? <LoadingDots /> : error ? 'Retry' : 'Load more'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
